import { translate } from "app/filters/i18nFilter";

export enum Intent {
    Informational = "informational",
    Navigational = "navigational",
    Local = "local",
    Transactional = "transactional",
    JobSearch = "job_search",
}

// region filter

const createLazyMap = <T extends Record<string, () => string>>(map: T): Record<keyof T, string> => {
    const cache = new Map<string, string>();
    return new Proxy({} as Record<keyof T, string>, {
        get(_target, prop: string) {
            if (cache.has(prop)) {
                return cache.get(prop);
            }
            const value = map[prop]?.();
            if (value !== undefined) {
                cache.set(prop, value);
                return value;
            }
            return undefined;
        },
    });
};

export const MAP_INTENT_TO_CHIPDOWN_TEXT = createLazyMap({
    [Intent.Informational]: () => translate("keywords.table.filters.intent.informational"),
    [Intent.Navigational]: () => translate("keywords.table.filters.intent.navigational"),
    [Intent.Local]: () => translate("keywords.table.filters.intent.local"),
    [Intent.Transactional]: () => translate("keywords.table.filters.intent.transactional"),
    [Intent.JobSearch]: () => translate("keywords.table.filters.intent.job.search"),
});

const MAP_INTENT_TO_QUERY_TEXT = {
    [Intent.Informational]: "informational",
    [Intent.Navigational]: "navigational",
    [Intent.Local]: "local",
    [Intent.Transactional]: "transactional",
    [Intent.JobSearch]: "job_search",
};

const createChipdownTextToQueryTextMap = () => {
    const cache = new Map<string, string>();
    return new Proxy({} as Record<string, string>, {
        get(_target, prop: string) {
            if (cache.has(prop)) {
                return cache.get(prop);
            }
            const intentEntries = Object.values(Intent) as Intent[];
            for (const intent of intentEntries) {
                const chipdownText = MAP_INTENT_TO_CHIPDOWN_TEXT[intent];
                if (chipdownText === prop) {
                    const queryText = MAP_INTENT_TO_QUERY_TEXT[intent];
                    cache.set(prop, queryText);
                    return queryText;
                }
            }
            return undefined;
        },
    });
};

export const MAP_CHIPDOWN_TEXT_TO_INTENT_QUERY_TEXT = createChipdownTextToQueryTextMap();

export const MAP_INTENT_TO_CHIPDOWN_TOOLTIP_TEXT = createLazyMap({
    [Intent.Informational]: () => translate("keywords.table.filters.intent.cell.tooltip.intent.information"),
    [Intent.Navigational]: () => translate("keywords.table.filters.intent.cell.tooltip.intent.navigational"),
    [Intent.Local]: () => translate("keywords.table.filters.intent.cell.tooltip.intent.local"),
    [Intent.Transactional]: () => translate("keywords.table.filters.intent.cell.tooltip.intent.transactional"),
    [Intent.JobSearch]: () => translate("keywords.table.filters.intent.cell.tooltip.intent.job.search"),
});

// endregion

// region table cell

export const INTENT_CELL_REPRESENTATION = {
    [Intent.Navigational]: "NAV",
    [Intent.Informational]: "INFO",
    [Intent.Transactional]: "TRANSAC.",
    [Intent.Local]: "LOCAL",
    [Intent.JobSearch]: "JOB",
};

export const INTENT_PAYLOAD_VALUE = {
    [Intent.Navigational]: "Navigational",
    [Intent.Informational]: "Informational",
    [Intent.Transactional]: "Transactional",
    [Intent.Local]: "Local",
    [Intent.JobSearch]: "Job_Search",
};

export const INTENT_PAYLOAD_TO_TOOLTIP_INFORMATION_FULL_TEXT = createLazyMap({
    [INTENT_PAYLOAD_VALUE[Intent.Informational]]: () =>
        translate("keywords.table.filters.intent.cell.tooltip.full.intent.information"),
    [INTENT_PAYLOAD_VALUE[Intent.Navigational]]: () =>
        translate("keywords.table.filters.intent.cell.tooltip.full.intent.navigational"),
    [INTENT_PAYLOAD_VALUE[Intent.Local]]: () =>
        translate("keywords.table.filters.intent.cell.tooltip.full.intent.local"),
    [INTENT_PAYLOAD_VALUE[Intent.Transactional]]: () =>
        translate("keywords.table.filters.intent.cell.tooltip.full.intent.transactional"),
    [INTENT_PAYLOAD_VALUE[Intent.JobSearch]]: () =>
        translate("keywords.table.filters.intent.cell.tooltip.full.intent.job.search"),
});

export const MAP_INTENT_PAYLOAD_TO_INTENT_REPRESENTATION = {
    [INTENT_PAYLOAD_VALUE[Intent.Informational]]: INTENT_CELL_REPRESENTATION[Intent.Informational],
    [INTENT_PAYLOAD_VALUE[Intent.Navigational]]: INTENT_CELL_REPRESENTATION[Intent.Navigational],
    [INTENT_PAYLOAD_VALUE[Intent.Transactional]]: INTENT_CELL_REPRESENTATION[Intent.Transactional],
    [INTENT_PAYLOAD_VALUE[Intent.Local]]: INTENT_CELL_REPRESENTATION[Intent.Local],
    [INTENT_PAYLOAD_VALUE[Intent.JobSearch]]: INTENT_CELL_REPRESENTATION[Intent.JobSearch],
};

export const MAP_INTENT_PAYLOAD_TO_INTENT_TOOLTIP_DISCLAIMER = createLazyMap({
    [INTENT_PAYLOAD_VALUE[Intent.Informational]]: () =>
        translate("keywords.table.filters.intent.cell.tooltip.intent.information"),
    [INTENT_PAYLOAD_VALUE[Intent.Navigational]]: () =>
        translate("keywords.table.filters.intent.cell.tooltip.intent.navigational"),
    [INTENT_PAYLOAD_VALUE[Intent.Transactional]]: () =>
        translate("keywords.table.filters.intent.cell.tooltip.intent.transactional"),
    [INTENT_PAYLOAD_VALUE[Intent.Local]]: () => translate("keywords.table.filters.intent.cell.tooltip.intent.local"),
    [INTENT_PAYLOAD_VALUE[Intent.JobSearch]]: () =>
        translate("keywords.table.filters.intent.cell.tooltip.intent.job.search"),
});

// endregion

const createIntentPayloadToOverviewRepresentationMap = () => {
    const cache = new Map<string, string>();
    return new Proxy({} as Record<string, string>, {
        get(_target, prop: string) {
            if (cache.has(prop)) {
                return cache.get(prop);
            }
            const intentEntries = Object.values(Intent) as Intent[];
            for (const intent of intentEntries) {
                if (INTENT_PAYLOAD_VALUE[intent] === prop) {
                    const chipdownText = MAP_INTENT_TO_CHIPDOWN_TEXT[intent];
                    cache.set(prop, chipdownText);
                    return chipdownText;
                }
            }
            return undefined;
        },
    });
};

const MAP_INTENT_PAYLOAD_VALUE_TO_OVERVIEW_REPRESENTATION = createIntentPayloadToOverviewRepresentationMap();
